import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Card } from '@atoms/index';
import { apiClient } from '@services/api';
import { useAuthStore } from '@store/authStore';
import { storage, STORAGE_KEYS } from '@utils/storage';

/**
 * Terms Acceptance Page
 * Final step of registration - User accepts terms and conditions
 */
const TermsAcceptancePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email } = (location.state as { email?: string }) || {};
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setAuth } = useAuthStore();

  // Redirect if no email
  if (!email) {
    navigate('/register');
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!termsAccepted) {
      setError('Kullanım koşullarını kabul etmeniz gerekmektedir.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.post('/auth/accept-terms', {
        email,
        terms_accepted: true,
      });

      const { access_token, refresh_token, user } = response.data.data;

      // Store tokens in auth store (using same structure as socialLogin)
      const tokens = {
        access_token,
        refresh_token,
      };
      const userData = {
        id: user.id,
        email: user.email,
        name: user.name,
        surname: user.surname,
      };

      // Store tokens and user data
      storage.set(STORAGE_KEYS.AUTH_TOKENS, tokens);
      storage.set(STORAGE_KEYS.USER_DATA, userData);

      // Update store state
      useAuthStore.setState({
        user: userData,
        tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      // Redirect to home page
      navigate('/');
    } catch (err: any) {
      console.error('Terms acceptance error:', err);
      setError(
        err.response?.data?.error?.message ||
          'Kullanım koşulları kabul edilemedi. Lütfen tekrar deneyin.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-8">
        <div className="max-w-2xl w-full">
          {/* Header */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-block p-4 rounded-3xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-premium mb-6">
              <svg
                className="w-12 h-12 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Kullanım Koşulları</h1>
            <p className="text-gray-600 text-lg">Son adım! Devam etmek için koşulları kabul edin</p>
          </div>

          {/* Card */}
          <Card padding="none" className="overflow-hidden backdrop-blur-xl animate-scale-in">
            <div className="p-8">
              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 rounded-xl bg-error/10 border border-error/30">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-error flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-error font-medium">{error}</p>
                  </div>
                </div>
              )}

              {/* Terms Content */}
              <div className="mb-6 max-h-96 overflow-y-auto pr-2">
                <div className="prose prose-sm max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Lezzet Yolda Kullanım Koşulları ve Gizlilik Politikası Özeti
                  </h3>

                  <div className="space-y-4 text-gray-700">
                    <p className="text-sm leading-relaxed">
                      Platformu kullanarak 6563 sayılı E-Ticaret Kanunu ve 6502 sayılı Tüketici
                      Kanunu kapsamındaki Kullanım Koşullarımızı; 6698 sayılı KVKK kapsamındaki
                      Gizlilik Politikası ve Aydınlatma Metnimizi kabul etmiş sayılırsınız.
                    </p>
                    <p className="text-sm leading-relaxed">
                      <strong>Özet:</strong> Üyelik ve hesap güvenliği sizin sorumluluğunuzdadır.
                      Siparişler onay sonrası işleme alınır; ödeme güvenli altyapı ile yapılır.
                      Yemek siparişlerinde cayma hakkı 6502 sayılı Kanun uyarınca sınırlıdır.
                      Kişisel verileriniz KVKK&apos;ya uygun işlenir; haklarınız için Gizlilik
                      Politikamıza bakınız.
                    </p>
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">
                      <strong>Önemli:</strong> Tam metni okumak için aşağıdaki linklere tıklayın.
                      Bu koşulları kabul ederek Kullanım Koşulları ve Gizlilik Politikasının tam
                      metnini okuduğunuzu ve kabul ettiğinizi beyan edersiniz.
                    </p>
                  </div>
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={isLoading}
                    className="mt-1 w-5 h-5 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                    <span className="font-medium">
                      Kullanım Koşulları ve Gizlilik Politikasını okudum ve kabul ediyorum.
                    </span>{' '}
                    <span className="text-error">*</span>
                  </span>
                </label>
              </div>

              {/* Submit Form */}
              <form onSubmit={handleSubmit}>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isLoading || !termsAccepted}
                  isLoading={isLoading}
                >
                  Kullanım Koşullarını Kabul Et ve Devam Et
                </Button>
              </form>

              {/* Links */}
              <div className="mt-6 text-center space-x-4">
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Kullanım Koşulları (Tam Metin)
                </Link>
                <span className="text-gray-300">|</span>
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Gizlilik Politikası ve KVKK
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default TermsAcceptancePage;
